import { useCallback } from "react";
import { useLocation } from "@tanstack/react-router";
import { useLatestRef } from "@/shared/hooks/useLatestRef";
import { getScreenContext } from "../context/screenContext";
import type { useAgentBudget } from "./useAgentBudget";
import type { useAgentChat } from "./useAgentChat";

type ChatSend = Pick<ReturnType<typeof useAgentChat>, "streaming" | "send">;
type BudgetGate = Pick<ReturnType<typeof useAgentBudget>, "level" | "refresh">;

export type AgentSendOptions = Readonly<{ interrupt?: boolean }>;

/**
 * The one way to send a turn from either assistant surface: trim, drop no-ops
 * and turns that cannot run (mid-stream or over the daily budget), resolve the
 * screen context at read time so the agent sees the screen as it is right then
 * (message send and mid-turn get_screen_context alike), and refresh the budget
 * once the turn settles. Shared by the docked drawer and the full-page
 * workspace so their send semantics never drift.
 *
 * Voice barge-in passes `{ interrupt: true }` so a live run is aborted and the
 * new utterance is sent. Typed send stays a two-argument `chat.send` call.
 */
export function useAgentSend(
  chat: ChatSend,
  budget: BudgetGate,
): (raw: string, options?: AgentSendOptions) => void {
  const location = useLocation();
  const { streaming, send } = chat;
  const { level, refresh } = budget;

  // Mirror the live router location so the screen-context getter reads the
  // current screen at call time — a mid-turn navigate_to must not leave
  // get_screen_context reporting the pre-navigation route.
  const locationRef = useLatestRef(location);

  return useCallback(
    (raw: string, options?: AgentSendOptions) => {
      const text = raw.trim();
      if (!text || level === "exhausted") {
        return;
      }
      if (streaming && options?.interrupt !== true) {
        return;
      }
      const getContext = () =>
        getScreenContext(locationRef.current.pathname, locationRef.current.searchStr);
      const run =
        options?.interrupt === true ? send(text, getContext, options) : send(text, getContext);
      void run.then(() => refresh());
    },
    // Stable ref. Listing the live pathname would recreate send on every navigation.
    // oxlint-disable-next-line react/exhaustive-deps
    [streaming, level, send, refresh],
  );
}
